/**
 * Color schemes for all Pawn components
 *
 * Defines the color variants and gradient configurations for each player's pawn
 * (Red, Blue, Yellow, Green). These schemes are used by the generic PawnComponent.
 */

export interface PawnGradientStop {
  offset?: string;
  stopColor?: string;
  stopOpacity?: string;
}

export interface PawnGradientConfig {
  id: string;
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  stops: PawnGradientStop[];
}

export interface PawnColorScheme {
  name: string;
  ariaLabel: string;
  gradients: {
    shadow1: PawnGradientConfig;
    base1: PawnGradientConfig;
    base2: PawnGradientConfig;
    ball1: PawnGradientConfig;
    ball2: PawnGradientConfig;
    detail1: PawnGradientConfig;
    detail2: PawnGradientConfig;
  };
  solidColors: {
    baseMidSection: string;
    ballConnection: string;
    ballSeparator: string;
  };
}

export const PAWN_COLOR_SCHEMES: Record<string, PawnColorScheme> = {
  red: {
    name: 'red',
    ariaLabel: 'Red player pawn',
    gradients: {
      shadow1: {
        id: 'redPawnShadow1',
        x1: '161.12',
        y1: '53.29',
        x2: '161.12',
        y2: '419.37',
        stops: [{ offset: '0.54', stopOpacity: '0' }, { offset: '1' }]
      },
      base1: {
        id: 'redPawnBase1',
        x1: '48.7402',
        y1: '323.54',
        x2: '273.5',
        y2: '323.54',
        stops: [{ stopColor: '#C1272D' }, { offset: '1', stopColor: '#ED1C24' }]
      },
      base2: {
        id: 'redPawnBase2',
        x1: '159.9',
        y1: '193.81',
        x2: '159.9',
        y2: '399.09',
        stops: [
          { offset: '0.53', stopColor: '#FF0000' },
          { offset: '0.76', stopColor: '#ED1C24' },
          { offset: '1', stopColor: '#6A272D' }
        ]
      },
      ball1: {
        id: 'redPawnBall1',
        x1: '88.22',
        y1: '274.48',
        x2: '234.03',
        y2: '274.48',
        stops: [
          { stopColor: '#ED1C24' },
          { offset: '0.51', stopColor: '#C1272D' },
          { offset: '1', stopColor: '#ED1C24' }
        ]
      },
      ball2: {
        id: 'redPawnBall2',
        x1: '77.5144',
        y1: '232.382',
        x2: '231.182',
        y2: '237.426',
        stops: [
          { stopColor: '#C1272D' },
          { offset: '0.51', stopColor: '#ED1C24' },
          { offset: '1', stopColor: '#C1272D' }
        ]
      },
      detail1: {
        id: 'redPawnDetail1',
        x1: '176.434',
        y1: '47.2336',
        x2: '136.807',
        y2: '257.553',
        stops: [
          { stopColor: '#EDEEF0' },
          { offset: '0.6', stopColor: '#E4E5E7' },
          { offset: '0.99', stopColor: '#C7C8CA' }
        ]
      },
      detail2: {
        id: 'redPawnDetail2',
        x1: '175.127',
        y1: '50.0097',
        x2: '136.832',
        y2: '253.264',
        stops: [
          { stopColor: '#72070F' },
          { offset: '0.37', stopColor: '#ED1C24' },
          { offset: '1', stopColor: '#4C0309' }
        ]
      }
    },
    solidColors: {
      baseMidSection: '#511212',
      ballConnection: '#EF7070',
      ballSeparator: 'black'
    }
  },
  blue: {
    name: 'blue',
    ariaLabel: 'Blue player pawn',
    gradients: {
      shadow1: {
        id: 'bluePawnShadow1',
        x1: '161.14',
        y1: '55.22',
        x2: '161.14',
        y2: '420.14',
        stops: [{ offset: '0.54', stopOpacity: '0' }, { offset: '1' }]
      },
      base1: {
        id: 'bluePawnBase1',
        x1: '49.1201',
        y1: '324.6',
        x2: '273.16',
        y2: '324.6',
        stops: [{ stopColor: '#2E3192' }, { offset: '1', stopColor: '#1B1464' }]
      },
      base2: {
        id: 'bluePawnBase2',
        x1: '159.92',
        y1: '195.3',
        x2: '159.92',
        y2: '399.91',
        stops: [
          { offset: '0.53', stopColor: '#0048FF' },
          { offset: '0.76', stopColor: '#0000FF' },
          { offset: '1', stopColor: '#2E3192' }
        ]
      },
      ball1: {
        id: 'bluePawnBall1',
        x1: '88.47',
        y1: '275.7',
        x2: '233.81',
        y2: '275.7',
        stops: [
          { stopColor: '#00006B' },
          { offset: '0.51', stopColor: '#000036' },
          { offset: '1', stopColor: '#00006B' }
        ]
      },
      ball2: {
        id: 'bluePawnBall2',
        x1: '77.81',
        y1: '233.743',
        x2: '230.978',
        y2: '238.77',
        stops: [
          { stopColor: '#2E3192' },
          { offset: '0.51', stopColor: '#0000FF' },
          { offset: '1', stopColor: '#2E3192' }
        ]
      },
      detail1: {
        id: 'bluePawnDetail1',
        x1: '49.0801',
        y1: '153.54',
        x2: '267.05',
        y2: '153.54',
        stops: [
          { stopColor: '#EDEEF0' },
          { offset: '0.6', stopColor: '#E4E5E7' },
          { offset: '0.99', stopColor: '#C7C8CA' }
        ]
      },
      detail2: {
        id: 'bluePawnDetail2',
        x1: '52.1102',
        y1: '154.32',
        x2: '262.75',
        y2: '154.32',
        stops: [
          { stopColor: '#2E3192' },
          { offset: '0.37', stopColor: '#0071BC' },
          { offset: '1', stopColor: '#1B1464' }
        ]
      }
    },
    solidColors: {
      baseMidSection: '#115255',
      ballConnection: '#7080F2',
      ballSeparator: 'black'
    }
  },
  yellow: {
    name: 'yellow',
    ariaLabel: 'Yellow player pawn',
    gradients: {
      shadow1: {
        id: 'yellowPawnShadow1',
        x1: '161.12',
        y1: '52.65',
        x2: '161.12',
        y2: '420.84',
        stops: [{ offset: '0.54', stopOpacity: '0' }, { offset: '1' }]
      },
      base1: {
        id: 'yellowPawnBase1',
        x1: '48.0801',
        y1: '324.46',
        x2: '274.16',
        y2: '324.46',
        stops: [{ stopColor: '#FBB03B' }, { offset: '1', stopColor: '#E9D700' }]
      },
      base2: {
        id: 'yellowPawnBase2',
        x1: '159.89',
        y1: '193.98',
        x2: '159.89',
        y2: '400.45',
        stops: [
          { offset: '0.53', stopColor: '#FCEE21' },
          { offset: '0.76', stopColor: '#FBB03B' },
          { offset: '1', stopColor: '#754C24' }
        ]
      },
      ball1: {
        id: 'yellowPawnBall1',
        x1: '87.79',
        y1: '275.12',
        x2: '234.45',
        y2: '275.12',
        stops: [
          { stopColor: '#D4C300' },
          { offset: '0.51', stopColor: '#6A5B00' },
          { offset: '1', stopColor: '#D4C300' }
        ]
      },
      ball2: {
        id: 'yellowPawnBall2',
        x1: '111.656',
        y1: '235.524',
        x2: '208.461',
        y2: '234.839',
        stops: [
          { stopColor: '#FBB03B' },
          { offset: '0.51', stopColor: '#FCEE21' },
          { offset: '1', stopColor: '#FBB03B' }
        ]
      },
      detail1: {
        id: 'yellowPawnDetail1',
        x1: '141.958',
        y1: '44.8202',
        x2: '172.252',
        y2: '259.827',
        stops: [
          { stopColor: '#EDEEF0' },
          { offset: '0.6', stopColor: '#E4E5E7' },
          { offset: '0.99', stopColor: '#C7C8CA' }
        ]
      },
      detail2: {
        id: 'yellowPawnDetail2',
        x1: '141.596',
        y1: '47.9109',
        x2: '170.872',
        y2: '255.689',
        stops: [
          { stopColor: '#FBB03B' },
          { offset: '0.37', stopColor: '#E2CC00' },
          { offset: '1', stopColor: '#FBB03B' }
        ]
      }
    },
    solidColors: {
      baseMidSection: '#545212',
      ballConnection: '#F2F270',
      ballSeparator: 'black'
    }
  },
  green: {
    name: 'green',
    ariaLabel: 'Green player pawn',
    gradients: {
      shadow1: {
        id: 'greenPawnShadow1',
        x1: '161.12',
        y1: '51.09',
        x2: '161.12',
        y2: '420.64',
        stops: [{ offset: '0.54', stopOpacity: '0' }, { offset: '1' }]
      },
      base1: {
        id: 'greenPawnBase1',
        x1: '47.6599',
        y1: '323.91',
        x2: '274.58',
        y2: '323.91',
        stops: [{ stopColor: '#098B6B' }, { offset: '1', stopColor: '#0B735C' }]
      },
      base2: {
        id: 'greenPawnBase2',
        x1: '159.89',
        y1: '192.94',
        x2: '159.89',
        y2: '400.18',
        stops: [
          { offset: '0.53', stopColor: '#25DE69' },
          { offset: '0.76', stopColor: '#14B16C' },
          { offset: '1', stopColor: '#0B735C' }
        ]
      },
      ball1: {
        id: 'greenPawnBall1',
        x1: '87.51',
        y1: '274.38',
        x2: '234.73',
        y2: '274.38',
        stops: [
          { stopColor: '#0F775D' },
          { offset: '0.51', stopColor: '#095552' },
          { offset: '1', stopColor: '#0F705A' }
        ]
      },
      ball2: {
        id: 'greenPawnBall2',
        x1: '76.7163',
        y1: '231.876',
        x2: '231.863',
        y2: '236.969',
        stops: [
          { stopColor: '#0CAA69' },
          { offset: '0.51', stopColor: '#0EA569' },
          { offset: '1', stopColor: '#06B265' }
        ]
      },
      detail1: {
        id: 'greenPawnDetail1',
        x1: '48.0933',
        y1: '148.893',
        x2: '265.086',
        y2: '156.015',
        stops: [
          { stopColor: '#EDEEF0' },
          { offset: '0.6', stopColor: '#E4E5E7' },
          { offset: '0.99', stopColor: '#C7C8CA' }
        ]
      },
      detail2: {
        id: 'greenPawnDetail2',
        x1: '51.076',
        y1: '149.771',
        x2: '260.773',
        y2: '156.654',
        stops: [
          { stopColor: '#006837' },
          { offset: '0.37', stopColor: '#39B54A' },
          { offset: '1', stopColor: '#006837' }
        ]
      }
    },
    solidColors: {
      baseMidSection: '#115255',
      ballConnection: '#6EF374',
      ballSeparator: 'black'
    }
  }
};

export type PawnColor = keyof typeof PAWN_COLOR_SCHEMES;
